import { getDocumentOrShadowRoot, getVisible } from '../utilities/DomUtilities';

const PORTAL_HEADER_SELECTOR = 'portal-header';

const SELECTORS = {
    searchInput: '[data-testid="searchbox-input-group"] input[type="search"]',
    searchButton: '[data-testid="searchbox-input-group"] input[type="submit"]',
    toggleButton: '[data-testid="header-search-toggle-button"]',
    loginButton: '[data-testid="login-button"]',
    accountDropdown: '#account-nav-dropdown',
    headerForm: '#header-search-courses-form',
};

type HeaderElements = {
    toggleButton: HTMLElement | null;
    searchInput: HTMLInputElement | null;
    searchButton: HTMLInputElement | null;
    loginButton: HTMLElement | null;
    accountDropdown: HTMLElement | null;
};

const getTargetType = (target: HTMLElement) => ({
    isSearchInput:
        target.tagName === 'INPUT' &&
        target.getAttribute('type') === 'search' &&
        !!target.closest(SELECTORS.headerForm),

    isSearchButton: target.dataset?.testid === 'search-button' && !!target.closest(SELECTORS.headerForm),

    isAccountOrLogin: target.id === 'account-nav-dropdown' || target.dataset?.testid === 'login-button',

    isHideSearch: target.title === 'Hide search',
});

const getHeaderElements = (): HeaderElements => {
    const root = getDocumentOrShadowRoot(PORTAL_HEADER_SELECTOR);
    if (!root) {
        return {
            toggleButton: null,
            searchInput: null,
            searchButton: null,
            loginButton: null,
            accountDropdown: null,
        };
    }

    const headerForm = root.querySelector<HTMLFormElement>(SELECTORS.headerForm);

    const toggleButton = getVisible(root.querySelectorAll<HTMLElement>(SELECTORS.toggleButton));
    const searchInput = headerForm?.querySelector<HTMLInputElement>(SELECTORS.searchInput) ?? null;
    const searchButton = headerForm?.querySelector<HTMLInputElement>(SELECTORS.searchButton) ?? null;
    const loginButton = getVisible(root.querySelectorAll<HTMLElement>(SELECTORS.loginButton));
    const accountDropdown = getVisible(root.querySelectorAll<HTMLElement>(SELECTORS.accountDropdown));

    return {
        toggleButton,
        searchInput,
        searchButton,
        loginButton,
        accountDropdown,
    };
};

const handleForwardTab = (
    e: KeyboardEvent,
    targetType: ReturnType<typeof getTargetType>,
    els: ReturnType<typeof getHeaderElements>,
) => {
    const { isSearchInput, isSearchButton, isAccountOrLogin } = targetType;

    if (isSearchInput) {
        e.preventDefault();
        els.searchButton?.focus();
    } else if (isSearchButton) {
        e.preventDefault();
        els.toggleButton?.focus();
    } else if (isAccountOrLogin) {
        e.preventDefault();
        els.searchInput?.focus();
    }
};

const handleReverseTab = (
    e: KeyboardEvent,
    targetType: ReturnType<typeof getTargetType>,
    els: ReturnType<typeof getHeaderElements>,
) => {
    const { isHideSearch, isSearchButton, isSearchInput } = targetType;

    if (isHideSearch) {
        e.preventDefault();
        els.searchButton?.focus();
    } else if (isSearchButton) {
        e.preventDefault();
        els.searchInput?.focus();
    } else if (isSearchInput) {
        e.preventDefault();
        (els.loginButton || els.accountDropdown)?.focus();
    }
};

const getSearchToggleFocusableElement = (show: boolean) => {
    const root = getDocumentOrShadowRoot(PORTAL_HEADER_SELECTOR);
    if (!root) return;
    if (show) {
        const searchInput = root.querySelector<HTMLInputElement>(SELECTORS.searchInput);
        searchInput?.focus();
    } else {
        const toggleButton = getVisible(root.querySelectorAll<HTMLElement>(SELECTORS.toggleButton));
        toggleButton?.focus();
    }
};

export const headerAccessibility = {
    getSearchToggleFocusableElement,
    getHeaderElements,
    getTargetType,
    handleForwardTab,
    handleReverseTab,
};
